// ============================================================================
// avecRetentativa: o que é falha passageira do Avec e quanto esperar (A2a do
// "Dashboard sem zero silencioso", 23/10/2026). Funções puras, sem rede, sem
// React e sem import.meta.env (testáveis direto no node).
// ============================================================================
// Regras do dono que isto respeita:
//   - a nova tentativa fica só para o que a pessoa está VENDO (prioridade 0 e 1)
//     e para o botão "Tentar de novo" (prioridade 0). O sync de 60 s, a grade
//     diária e os 12 meses (prioridade 2 e 3) tentam UMA vez: a próxima rodada
//     de 60 s já é a nova tentativa, e as guardas seguram o valor anterior;
//   - timeout repete no máximo 1 vez (a função da Vercel continua rodando
//     depois que o aparelho desiste, H14 viu "Task timed out after 300 s");
//   - disjuntor: muitas recusas por limite (403 do Avec, 429, timeout) em pouco
//     tempo derrubam todo mundo para 1 tentativa e fazem o fundo falhar rápido.
// ============================================================================

export const PRIORIDADE = Object.freeze({
  ATUAL: 0,       // período que a tela mostra agora, "hoje" do profissional, Tentar de novo
  ANTERIOR: 1,    // período de comparação; telas sem prioridade própria (padrão)
  FUNDO: 2,       // sync mensal de 60 s, grade diária
  HISTORICO: 3,   // 12 meses e re-sincronização de mês com falha
});

export const POLITICA_RETENTATIVA = Object.freeze({
  tentativasVisiveis: 3,   // prioridade 0 e 1
  tentativasFundo: 1,      // prioridade >= 2
  maxTimeouts: 2,          // 1 repetição depois de um timeout
  baseMs: 600,
  fator: 2,
  jitterMs: 400,
  tetoEsperaMs: 4000,
});

export function tentativasPara(prioridade, { disjuntorAberto = false } = {}) {
  if (disjuntorAberto) return 1;
  const p = Number.isFinite(prioridade) ? prioridade : PRIORIDADE.ANTERIOR;
  return p <= PRIORIDADE.ANTERIOR ? POLITICA_RETENTATIVA.tentativasVisiveis : POLITICA_RETENTATIVA.tentativasFundo;
}

// Motivos que contam para o disjuntor e para baixar o teto do limitador.
export const MOTIVOS_DE_LIMITE = new Set(['http_403_avec', 'http_429', 'timeout']);
// Revisão 23/10/2026 (achado 21): sinais de Avec/proxy FORA DO AR. Contam num
// segundo critério do disjuntor que só corta a nova tentativa (todo mundo com 1
// tentativa enquanto estiver aberto); não faz o fundo falhar rápido. Antes desta
// rodada um 502 ia 1 vez (o laço antigo só repetia 429/503); sem isto, numa queda
// toda chamada visível ia 3 vezes.
export function ehForaDoAr(motivo) {
  const m = String(motivo || '');
  if (m === 'http_5xx' || m === 'rede' || m === 'corpo_invalido') return true;
  const c = /^avec_code_(\d+)$/.exec(m);
  return !!c && Number(c[1]) >= 500;
}

/**
 * Classifica a resposta de UMA tentativa ao /api/avec.
 * @returns null quando é sucesso; senão { motivo, transitoria, code }.
 *   motivos: rede, timeout, http_429, http_5xx, http_403_avec, http_403_portao,
 *   http_401, http_4xx, corpo_invalido, avec_code_<n>.
 * `exigeReport` (AVEC_EXIGE_REPORT): 2xx JSON sem data.report também é falha.
 * Fica desligado até conferirem o envelope de período vazio legítimo de cada
 * relatório (sem isso, um zero legítimo viraria falha permanente).
 */
export function classificarFalha({ status, corpo = null, naoJson = false, cabecalhoUpstream = null, erro = null, exigeReport = false } = {}) {
  if (erro) {
    if (erro.name === 'AbortError' || erro.name === 'TimeoutError') return { motivo: 'timeout', transitoria: true, code: null };
    return { motivo: 'rede', transitoria: true, code: null };
  }
  const st = Number(status);
  if (st === 429) return { motivo: 'http_429', transitoria: true, code: 429 };
  if (st >= 500) return { motivo: 'http_5xx', transitoria: true, code: st };
  if (st === 403) {
    // 403 do Avec (limite de consultas; o nginx manda HTML) x 403 do portão do
    // proxy (relatório financeiro, JSON e sem o cabeçalho do upstream).
    const doAvec = naoJson || String(cabecalhoUpstream ?? '') === '403';
    return doAvec
      ? { motivo: 'http_403_avec', transitoria: true, code: 403 }
      : { motivo: 'http_403_portao', transitoria: false, code: 403 };
  }
  if (st === 401) return { motivo: 'http_401', transitoria: false, code: 401 };
  if (st >= 400) return { motivo: 'http_4xx', transitoria: false, code: st };
  // 2xx
  if (naoJson || !corpo || typeof corpo !== 'object' || Array.isArray(corpo)) {
    return { motivo: 'corpo_invalido', transitoria: true, code: st || null };
  }
  const code = Number(corpo.code);
  if (corpo.code != null && Number.isFinite(code) && code >= 400) {
    return { motivo: `avec_code_${code}`, transitoria: code === 429 || code >= 500, code };
  }
  if (exigeReport && (!corpo.data || typeof corpo.data !== 'object' || !corpo.data.report || typeof corpo.data.report !== 'object')) {
    return { motivo: 'corpo_invalido', transitoria: true, code: st || null };
  }
  return null;
}

/** Retry-After em segundos (só número inteiro); senão null. */
export function lerRetryAfter(valor) {
  const v = String(valor ?? '').trim();
  return /^\d{1,4}$/.test(v) ? Number(v) : null;
}

/**
 * Espera antes da tentativa `n` (n >= 2). Retry-After (> 0 s, limitado ao teto)
 * quando veio; senão crescente com jitter: n=2 ≈ 600-1000 ms, n=3 ≈ 1200-1600 ms.
 * Retry-After: 0 usa a espera normal com jitter (revisão 23/10/2026, achado 22).
 */
export function esperaAntesDaTentativa(n, { retryAfterS = null, aleatorio = Math.random, politica = POLITICA_RETENTATIVA } = {}) {
  if (Number.isFinite(retryAfterS) && retryAfterS > 0) return Math.min(politica.tetoEsperaMs, retryAfterS * 1000);
  const passo = Math.max(0, n - 2);
  const base = politica.baseMs * Math.pow(politica.fator, passo);
  const jitter = Math.floor(Math.max(0, Math.min(0.999999, aleatorio())) * politica.jitterMs);
  return Math.min(politica.tetoEsperaMs, base + jitter);
}

/** Retry-After maior que o teto (ou que o prazo): não vale repetir agora (achado 22). */
export function retryAfterPassaDoTeto(retryAfterS, politica = POLITICA_RETENTATIVA) {
  return Number.isFinite(retryAfterS) && retryAfterS * 1000 > politica.tetoEsperaMs;
}

/**
 * Disjuntor por aparelho: `limite` recusas por limite (MOTIVOS_DE_LIMITE)
 * dentro de `janelaMs` abrem o disjuntor por `aberturaMs` (aberto(): todo mundo
 * com 1 tentativa e o fundo SEM CDN falha rápido). Segundo critério (achado 21):
 * `limite` sinais de fora do ar (ehForaDoAr) na mesma janela abrem foraDoAr()
 * pelo mesmo tempo, que só corta a nova tentativa.
 */
export function criarDisjuntor({ limite = 6, janelaMs = 10_000, aberturaMs = 15_000, agora = () => Date.now() } = {}) {
  let eventos = [];
  let abertoAte = 0;
  let eventosFora = [];
  let foraAte = 0;
  return {
    registrar(motivo) {
      const t = agora();
      if (ehForaDoAr(motivo)) {
        eventosFora = eventosFora.filter(x => t - x < janelaMs);
        eventosFora.push(t);
        if (eventosFora.length >= limite && t >= foraAte) { foraAte = t + aberturaMs; eventosFora = []; }
        return false;
      }
      if (!MOTIVOS_DE_LIMITE.has(motivo)) return false;
      eventos = eventos.filter(x => t - x < janelaMs);
      eventos.push(t);
      if (eventos.length >= limite && t >= abertoAte) {
        abertoAte = t + aberturaMs;
        eventos = [];
        return true;
      }
      return false;
    },
    aberto() { return agora() < abertoAte; },
    foraDoAr() { return agora() < foraAte; },
    abertoAte() { return abertoAte; },
    reiniciar() { eventos = []; abertoAte = 0; eventosFora = []; foraAte = 0; },
  };
}
